import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// KYC, documents and cheques for one client. Files are stored privately on R2
// and opened through the logged-in API (fetched as a blob), never a public link.
const LABELS = {
  meeting_photo: 'Meeting photo',
  pan: 'PAN card', aadhaar: 'Aadhaar', passport: 'Passport', photo: 'Photo', address_proof: 'Address proof', oci_pio: 'OCI / PIO',
  cheque: 'Cheque', payment_receipt: 'Payment receipt',
  booking_form: 'Booking form', cost_sheet: 'Cost sheet', allotment_letter: 'Allotment letter', agreement: 'Agreement', noc: 'NOC', other: 'Other',
};
const GROUPS = [['Meeting', ['meeting_photo']], ['KYC', ['pan', 'aadhaar', 'passport', 'photo', 'address_proof', 'oci_pio']], ['Payment', ['cheque', 'payment_receipt']],
  ['Document', ['booking_form', 'cost_sheet', 'allotment_letter', 'agreement', 'noc', 'other']]];
const inr = (v) => (v ? `₹${Number(v).toLocaleString('en-IN')}` : '—');
const size = (b) => { const n = Number(b) || 0; return n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`; };

const ClientDocuments = ({ apiBase, clientId }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ doc_type: 'pan', sale_id: '', cheque_number: '', bank_name: '', amount: '', cheque_date: '', notes: '' });
  const [file, setFile] = useState(null);
  const [inputKey, setInputKey] = useState(0);

  const load = useCallback(() => {
    axios.get(`${apiBase}/client-docs/${clientId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Could not load documents'));
  }, [apiBase, clientId]);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Choose a file'); return; }
    setBusy(true); setError('');
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(f).forEach(([k, v]) => { if (v !== '' && (f.doc_type === 'cheque' || !['cheque_number', 'bank_name', 'amount', 'cheque_date'].includes(k))) fd.append(k, v); });
    try {
      const r = await axios.post(`${apiBase}/client-docs/${clientId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      flash(r.data.message || 'Uploaded');
      setFile(null); setInputKey(inputKey + 1);
      setF({ ...f, cheque_number: '', bank_name: '', amount: '', cheque_date: '', notes: '' });
      load();
    } catch (err) {
      setError(err.response?.status === 403 ? 'Your role is not allowed to upload documents.' : (err.response?.data?.error || 'Upload failed'));
    } finally { setBusy(false); }
  };

  const open = async (doc) => {
    const w = window.open('', '_blank');
    try {
      const r = await axios.get(`${apiBase}/client-docs/file/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (w) w.location.href = url; else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (w) w.close();
      setError('Could not open the file');
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete ${LABELS[doc.doc_type] || 'this file'} (${doc.original_filename})?`)) return;
    try { await axios.delete(`${apiBase}/client-docs/file/${doc.id}`); flash('Deleted'); load(); }
    catch (e) { setError(e.response?.status === 403 ? 'Your role is not allowed to delete documents.' : 'Could not delete'); }
  };

  const field = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#4b5563', marginBottom: '4px' };
  const cell = { flex: '1 1 180px', minWidth: 0 };
  const docs = data?.documents || [];
  const bookings = data?.bookings || [];
  const payFor = (id) => (data?.payments || []).find((p) => p.id === id);
  const saleLabel = (id) => { const s = bookings.find((b) => b.id === id); return s ? `${s.unit_number}${s.booking_number ? ` · ${s.booking_number}` : ''}` : ''; };

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <h2 className="card-title">Documents & cheques</h2>
      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-4px' }}>Stored privately — files open only for logged-in staff.</p>

      <button type="button" className="btn btn-primary" style={{ marginBottom: '12px' }}
        onClick={() => { setF({ ...f, doc_type: 'meeting_photo' }); setTimeout(() => document.getElementById('cd-file')?.click(), 0); }}>
        📷 Take meeting photo
      </button>

      <form onSubmit={submit} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <div style={cell}>
            <label style={lbl}>Type</label>
            <select style={field} value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })}>
              {GROUPS.map(([g, list]) => (
                <optgroup key={g} label={g}>{list.map((t) => <option key={t} value={t}>{LABELS[t]}</option>)}</optgroup>
              ))}
            </select>
          </div>
          {bookings.length > 0 && (
            <div style={cell}>
              <label style={lbl}>Booking</label>
              <select style={field} value={f.sale_id} onChange={(e) => setF({ ...f, sale_id: e.target.value })}>
                <option value="">—</option>
                {bookings.map((b) => <option key={b.id} value={b.id}>{b.unit_number}{b.booking_number ? ` · ${b.booking_number}` : ''}</option>)}
              </select>
            </div>
          )}
          <div style={{ ...cell, flex: '2 1 260px' }}>
            <label style={lbl}>File (JPG, PNG, PDF · up to 15 MB)</label>
            <input id="cd-file" key={inputKey} style={{ ...field, padding: '6px' }} type="file" capture={f.doc_type === 'meeting_photo' ? 'user' : 'environment'}
              accept={f.doc_type === 'meeting_photo' ? 'image/*' : 'image/*,application/pdf'}
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          {f.doc_type === 'cheque' && (
            <>
              <div style={cell}><label style={lbl}>Cheque no.</label><input style={field} value={f.cheque_number} onChange={(e) => setF({ ...f, cheque_number: e.target.value })} /></div>
              <div style={cell}><label style={lbl}>Bank</label><input style={field} value={f.bank_name} onChange={(e) => setF({ ...f, bank_name: e.target.value })} /></div>
              <div style={cell}><label style={lbl}>Amount (₹) *</label><input style={field} type="number" min="0" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
              <div style={cell}><label style={lbl}>Cheque date</label><input style={field} type="date" value={f.cheque_date} onChange={(e) => setF({ ...f, cheque_date: e.target.value })} /></div>
            </>
          )}
          <div style={{ flex: '1 1 100%' }}><label style={lbl}>Notes</label><input style={field} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Uploading…' : f.doc_type === 'cheque' ? 'Upload cheque' : 'Upload'}</button>
        </div>
      </form>

      {msg && <div style={{ padding: '10px 14px', background: '#ecfdf5', color: '#065f46', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>{msg}</div>}
      {error && <div style={{ padding: '10px 14px', background: '#fef2f2', color: '#b91c1c', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

      {GROUPS.map(([g, list]) => {
        const rows = docs.filter((d) => list.includes(d.doc_type));
        return (
          <div key={g} style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>{g === 'Payment' ? 'Cheques & receipts' : g === 'Meeting' ? 'Meeting photos' : g === 'KYC' ? 'KYC' : 'Documents'} · {rows.length}</div>
            {rows.length === 0 ? <div style={{ fontSize: '13px', color: '#9ca3af' }}>Nothing uploaded yet.</div> : (
              <div className="table-container">
                <table className="table">
                  <tbody>
                    {rows.map((d) => {
                      const p = d.entity_type === 'payment' ? payFor(d.entity_id) : null;
                      return (
                        <tr key={d.id}>
                          <td><b>{LABELS[d.doc_type] || d.doc_type}</b><div style={{ fontSize: '11px', color: '#6b7280' }}>{d.original_filename} · {size(d.file_size)}</div></td>
                          <td style={{ fontSize: '12px' }}>
                            {p ? <>{inr(p.amount)}{p.reference_number ? ` · #${p.reference_number}` : ''}{p.bank_name ? ` · ${p.bank_name}` : ''}{p.payment_date ? ` · ${new Date(p.payment_date).toLocaleDateString('en-IN')}` : ''}{p.sale_id ? <div style={{ color: '#6b7280' }}>{saleLabel(p.sale_id)}</div> : null}</>
                              : d.entity_type === 'sale' ? saleLabel(d.entity_id) : ''}
                          </td>
                          <td style={{ fontSize: '12px', color: '#6b7280' }}>{new Date(d.created_at).toLocaleDateString('en-IN')}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ''}</td>
                          <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            <button type="button" className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '12px', marginRight: '6px' }} onClick={() => open(d)}>View</button>
                            <button type="button" className="btn btn-danger" style={{ padding: '4px 10px', fontSize: '12px' }} onClick={() => remove(d)}>Delete</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ClientDocuments;
